import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectApi, ToastStateHandler } from '../../../api';
import { ProjectRoleDto } from '../../../commonTypes/project.types';
import { useContainer } from '../../useContainer';
import { useTranslation } from '../../useTranslation';
import { setCachedProjectRoles, selectCachedProjectRoles } from '../../../store/slices/projects.slice';

const useHttpGetProjectRoles = (autoFetch: boolean = true) => {
    const { t } = useTranslation('newNotifications');
    const projectApi: ProjectApi = useContainer(ProjectApi);
    const dispatch = useDispatch();
    const cachedRoles = useSelector(selectCachedProjectRoles);
    const [roles, setRoles] = useState<ProjectRoleDto[]>([]);
    const [areProjectRolesReady, setProjectRolesReady] = useState<boolean>(false);

    const toastHandler: ToastStateHandler = useContainer(ToastStateHandler);

    const fetchRoles = useCallback(
        (forceRefresh = false) => {
            if (!forceRefresh && cachedRoles.length > 0) {
                setRoles(cachedRoles);
                setProjectRolesReady(true);
                return;
            }

            const { exec, abort } = projectApi.getRolesFromProject({
                onSuccess: res => {
                    dispatch(setCachedProjectRoles(res.body));
                    setRoles(res.body);
                },
                onError: _ => toastHandler.push('error', t('common.error'), t('common.somethingWentWrong')),
                onFinally: () => setProjectRolesReady(true),
            });
            exec();
            return abort;
        },
        [projectApi, toastHandler, t, cachedRoles, dispatch]
    );

    useEffect(() => {
        if (autoFetch) {
            if (cachedRoles.length > 0) {
                setRoles(cachedRoles);
                setProjectRolesReady(true);
                return;
            }

            fetchRoles();
        }
    }, [autoFetch]);

    return { roles, areProjectRolesReady, exec: fetchRoles };
};

export default useHttpGetProjectRoles;
